import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from "react-select";
import ReactAsyncSelect from "../controls/ReactAsyncSelect";
import api from '../../services/axios';
import { bankConcepts } from '../../services/bankConcepts';
import { useTranslation } from 'react-i18next';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import getBanks from '../../services/getBanks';
import getMovileurosAgents from '../../services/getMovileurosAgents';

const PREPAY_API = 'https://api2.prepayworld.com/api/secure';
const PREPAY_KEY = 'f446f91044ab672b251616785556d507798a22a1e6dc9c01887e49dcc73e40ef';
const PREPAY_CONCEPT_NAME = 'PREPAYWORLD';

const CreateBankEntry = (props) => {
  const { t } = useTranslation();
  const [date, setDate] = useState('');
  const [bankId, setBankId] = useState('');
  const [comment, setComment] = useState('');
  const [conceptId, setConceptId] = useState(null);
  const [conceptName, setConceptName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [amount, setAmount] = useState(0);
  const [prepayAgents, setPrepayAgents] = useState([]);
  const [prepayAgentId, setPrepayAgentId] = useState(null);
  const [prepayLoading, setPrepayLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [movileurosAgents, setMovileurosAgents] = useState([]);

  const isPrepayWorld = conceptName.toUpperCase().includes(PREPAY_CONCEPT_NAME);

  // Cargar agentes de PrepayWorld cuando se selecciona ese concepto
  useEffect(() => {
    if (!isPrepayWorld) { setPrepayAgentId(null); return; }
    setPrepayLoading(true);
    fetch(`${PREPAY_API}/admin/agents/list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Admin-Key': PREPAY_KEY }
    })
      .then(r => r.json())
      .then(res => {
        if (res.data) {
          setPrepayAgents(res.data.map(a => ({
            label: `${a.name} ${a.surname || ''} (${a.income_code}) - Saldo: ${a.balance}€`,
            value: a.agent_id
          })));
        }
      })
      .catch(e => setErrMsg('Error cargando agentes PrepayWorld'))
      .finally(() => setPrepayLoading(false));
  }, [isPrepayWorld]);

  const store = async (e) => {
    e.preventDefault();
    setErrMsg('');

    // Validar agente prepay si es ese concepto
    if (isPrepayWorld && !prepayAgentId) {
      setErrMsg('Selecciona el agente de PrepayWorld');
      return;
    }

    try {
      // 1. Guardar apunte bancario normal
      await api.post('/bank_entries', {
        date,
        amount,
        bankConceptId: conceptId ? conceptId : 1,
        bankId: bankId ? bankId : (props.loginParameters.company.includes('Demo')) ? 15 : 9,
        agentId,
        bankComment: comment,
      }, {
        headers: { 'Authorization': `Bearer ${props.loginParameters.accesToken}` }
      });

      // 2. Si es PrepayWorld → cargar saldo en prepay
      if (isPrepayWorld && prepayAgentId) {
        const prepayRes = await fetch(`${PREPAY_API}/admin/payments/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Key': PREPAY_KEY
          },
          body: JSON.stringify({
            agent_id: prepayAgentId,
            amount: parseFloat(amount),
            method: 'transfer',
            reference: comment || 'Ingreso desde panel.coinfru.com',
          })
        });
        const prepayData = await prepayRes.json();
        if (prepayData.errorCode !== 0) {
          setErrMsg(`Apunte guardado pero error en PrepayWorld: ${prepayData.message?.message_es}`);
          return;
        }
      }

      navigate('/bank_entries');
    } catch (err) {
      if (!err?.response) setErrMsg('No Server Response');
      else if (err.response?.status === 401) setErrMsg('Unauthorized');
      else setErrMsg('Error al guardar');
    }
  };

  const goBack = (e) => { e.preventDefault(); navigate('/bank_entries'); };

  useEffect(() => { setDate(new Date().toISOString().slice(0, 10)); }, []);
  useEffect(() => { getBanks(props.loginParameters.accesToken).then(setBanks); }, [props.loginParameters.accesToken]);
  useEffect(() => { getMovileurosAgents(props.loginParameters.accesToken).then(setMovileurosAgents); }, [props.loginParameters.accesToken]);

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <h3 className="text-center mb-4">{t('banks.create_bank_entry')}</h3>
          <form>
            <div className='mb-3'>
              <label className='form-label'>{t('banks.bank')}</label>
              <Select
                options={banks}
                defaultValue={(props.loginParameters.company.includes('Demo')) ? { label: "Demo Bank 1", value: 15 } : { label: "Cajamar", value: 9 }}
                onChange={(e) => setBankId(e.value)}
              />
            </div>
            <div className='mb-3'>
              <label className='form-label'>{t('banks.date')}</label>
              <input value={date} onChange={(e) => setDate(e.target.value)} type='date' className='form-control' />
            </div>
            <div className='mb-3'>
              <label className='form-label'>{t('banks.concept')}</label>
              <ReactAsyncSelect
                handleChange={(e) => { setConceptId(e.value); setConceptName(e.label); }}
                fetchData={bankConcepts}
                initialOption={{ label: "INGRESO CLIENTE EFECTIVO", value: 1 }}
              />
            </div>

            {/* Selector agente coinfru (oculto si es PrepayWorld) */}
            {!isPrepayWorld && (
              <div className='mb-3'>
                <label className='form-label'>{t('commercial_network.agent')}</label>
                <Select
                  options={movileurosAgents}
                  onChange={(e) => setAgentId(e.value)}
                  placeholder="Seleccionar agente..."
                  isClearable
                />
              </div>
            )}

            {/* Selector agente PrepayWorld */}
            {isPrepayWorld && (
              <div className='mb-3'>
                <label className='form-label'>🌍 Agente PrepayWorld</label>
                <Select
                  options={prepayAgents}
                  onChange={(e) => setPrepayAgentId(e ? e.value : null)}
                  placeholder={prepayLoading ? "Cargando agentes..." : "Seleccionar agente PrepayWorld..."}
                  isLoading={prepayLoading}
                  isClearable
                />
              </div>
            )}

            <div className='mb-3'>
              <label className='form-label'>{t('banks.amount')}</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value)} type='number' step="0.01" className='form-control' />
            </div>
            <div className='mb-3'>
              <label className='form-label'>{t('banks.comment')}</label>
              <input onChange={(e) => setComment(e.target.value)} type='text' className='form-control' />
            </div>

            {errMsg && <div className="alert alert-danger">{errMsg}</div>}

            <div className="d-flex justify-content-center gap-2 mt-4">
              <Button onClick={store} variant="success" type='submit'>{t('store')}</Button>
              <Button onClick={goBack} variant="primary" type='button'>{t('back')}</Button>
            </div>
          </form>
        </Col>
      </Row>
    </Container>
  );
};

export default CreateBankEntry;
